/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Resolve a `parentId` to something an operator recognises** (`P3a`).
 *
 * `parentId` holds an **objectId** — the durable identity, not the authored key — so the identity
 * strip needs a way from that to a name. Matching on the authored `id` instead would break the
 * moment somebody renames a parent, which is precisely what the durable id exists to prevent.
 *
 * **The hook owns its data source, and is not handed one.** That is the load-bearing decision.
 *
 * The obvious shape — pass in the artifacts the page already has — is wrong here, because the list
 * page holds a **filtered, paginated** slice. A parent on page 2, or excluded by a readiness filter,
 * would not be found, so the same source would read *"Extends House SSP"* on one screen and
 * *"Extends a parent"* on the next. **A chip that changes with the filter is a chip people
 * learn to ignore**, and it is exactly the drift one-projection exists to prevent — reintroduced
 * through the argument list rather than through a second implementation.
 *
 * So it queries the unfiltered set itself. React Query caches and dedupes it, so the several
 * surfaces rendering identity on one screen share a single request.
 *
 * **And it needs *both* lists, which is the defect this hook shipped with** (found 2026-08-26,
 * reviewing `#1166`). It read only `useDemandSources`, and the API excludes templates from that list
 * **for every actor** — *"a template is not a source anybody serves from; it is a baseline, and it
 * has its own surface."* So every template parent missed: `lookupParentName` returned `undefined`
 * and `parentIsTemplate` returned `false`, and a source extending a template rendered *"Extends
 * another source"* rather than *"From Publica"* — on the list, the detail page and the wizard alike.
 *
 * **A template parent is not an edge case, it is the main one.** `TemplatesPage` says so about its
 * own override gesture: *"a template override is exactly what P3a's inheritance is for"*. So the one
 * relation the chip most needed to name was the one it could never name.
 *
 * What hid it is that *"From {name}"* does appear — from `artifactIdentityChips`' **other** branch,
 * the wizard's session-scoped `appliedTemplateName`, which fires only when there is **no**
 * `parentId`. The label showed up exactly where nothing was stored and vanished exactly where
 * something was, which is the inverse of what the chip is for and reads as working.
 * `artifact-identity.test.ts` could not catch it either: it injects both lookups, so it proves the
 * chip renders *given* a working lookup and asserts nothing about whether one exists.
 *
 * **A parent the viewer cannot see still resolves to nothing**, because both endpoints filter by
 * visibility before the console sees anything — the templates list since `#1174`. That is the correct answer rather than a limitation:
 * naming an artifact they have no access to would be a disclosure, and the strip falls back to
 * *"Extends a parent"*, which still tells them this is not standalone.
 */

import { useMemo } from 'react';

import { useDemandSources, useDemandSourceTemplates } from './useDemandSources';
import { useReqStrategies, useReqStrategyTemplates } from './useReqStrategies';

export interface ArtifactLineage {
  lookupParentName: (objectId: string) => string | undefined;
  parentIsTemplate: (objectId: string) => boolean;
}

/** What a lineage lookup reads of an artifact: its durable id, its name, and whether it is a template. */
interface LineageMember {
  objectId?: string;
  name?: string;
  isTemplate?: boolean;
}

/**
 * **The lookup, for any type** (slice 7a). Each hook below owns its type's data source, for the reason
 * above; what they share is how a `parentId` becomes a name. Lists are read in order, so a later list
 * wins an overlap the same way every render rather than by whichever query settled first.
 */
export function lineageOf(...lists: ReadonlyArray<readonly LineageMember[] | undefined>): ArtifactLineage {
  const byObjectId = new Map<string, LineageMember>();
  for (const list of lists) {
    for (const artifact of list ?? []) {
      if (artifact?.objectId) byObjectId.set(artifact.objectId, artifact);
    }
  }
  return {
    lookupParentName: (objectId: string) => byObjectId.get(objectId)?.name || undefined,
    parentIsTemplate: (objectId: string) => byObjectId.get(objectId)?.isTemplate === true,
  };
}

/** The empty filter, hoisted so its identity is stable and the query key does not change per render. */
const UNFILTERED = {} as const;

/** A demand source's lineage: sources, then templates (disjoint, since the list excludes templates). */
export function useArtifactLineage(): ArtifactLineage {
  const { data } = useDemandSources(UNFILTERED);
  const { data: templates } = useDemandSourceTemplates();
  const items = data?.items;
  return useMemo(() => lineageOf(items, templates), [items, templates]);
}

/**
 * **A request strategy's lineage** (`RQ10`, slice 7a). Its parent is another strategy or a template, so
 * the lookup reads both lists, each holding every one of its kind this viewer may see.
 */
export function useReqStrategyLineage(): ArtifactLineage {
  const { data } = useReqStrategies();
  // And the templates, which the list leaves out (slice 7b): a strategy made from one names it.
  const { data: templates } = useReqStrategyTemplates();
  return useMemo(() => lineageOf(data, templates), [data, templates]);
}
